/** C3 — Menzies claim pack: tick the documents (OneDrive + LoanDisk / Monday / DocuSign), compile (summary PDF + zip → OneDrive / Court),
 *  review the email rendered from the Notifications template, then send from legal@ (gate G10). */
import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2, Mail, Package, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { compileMenziesPack, getMenziesState, sendMenziesPack } from "@/lib/api";
import { Field, FileDrop, UploadProgress, errMsg, fmtDate, money, uploadAll } from "./petitionShared";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

function CandidateList({ candidates, categories, picked, setPicked, disabled }) {
  const groups = useMemo(() => categories.map((c) => ({ ...c, rows: candidates.filter((f) => f.category === c.key && !f.is_pack_output) })).filter((g) => g.rows.length), [candidates, categories]);
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  if (!candidates.length) return <div className="text-[11px] text-muted-foreground border rounded p-3" data-testid="menzies-no-files">No documents found for this file yet — upload them to the customer's OneDrive folder or add them below.</div>;
  return (
    <div className="border rounded divide-y max-h-72 overflow-y-auto" data-testid="menzies-candidates">
      {groups.map((g) => (
        <div key={g.key} className="p-2">
          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1">{g.label} <span className="font-normal normal-case">· {g.rows.filter((r) => picked.includes(r.key)).length}/{g.rows.length}</span></div>
          {g.rows.map((f) => (
            <label key={f.key} className="flex items-start gap-2 py-0.5 text-[11px] cursor-pointer" data-testid={`menzies-file-${f.key.replace(/[^a-zA-Z0-9_-]/g, "_")}`}>
              <Checkbox checked={picked.includes(f.key)} onCheckedChange={() => toggle(f.key)} disabled={disabled} className="mt-0.5" />
              <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
              <span className="text-slate-400 shrink-0">{f.origin}{f.size ? ` · ${fmtSize(f.size)}` : ""}{f.linked ? " · petition" : ""}</span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

function PackSummary({ pack }) {
  if (!pack) return null;
  const c = pack.claim || {};
  return (
    <div className="border rounded p-2 bg-emerald-50/40 text-[11px] space-y-1" data-testid="menzies-pack-summary">
      <div className="flex items-center gap-2 flex-wrap">
        <Package className="w-3.5 h-3.5 text-emerald-700" /><strong>Pack compiled</strong> {fmtDate(pack.at)} by {pack.by} · {pack.files?.length} document{pack.files?.length === 1 ? "" : "s"} · {fmtSize(pack.zip?.size)}
        {pack.summary?.web_url && <a href={pack.summary.web_url} target="_blank" rel="noreferrer" className="text-indigo-700 inline-flex items-center gap-1 hover:underline" data-testid="menzies-summary-link"><ExternalLink className="w-3 h-3" />Claim summary PDF</a>}
        {pack.zip?.web_url && <a href={pack.zip.web_url} target="_blank" rel="noreferrer" className="text-indigo-700 inline-flex items-center gap-1 hover:underline" data-testid="menzies-zip-link"><ExternalLink className="w-3 h-3" />Zip</a>}
      </div>
      <div data-testid="menzies-claim-line">Claim <strong>{money(pack.claim_total)}</strong> = balance {money(c.balance)} + recoverable court costs {money(c.costs)}{c.interest != null ? ` · interest ${money(c.interest)}` : ""}{c.fees != null ? ` · fees ${money(c.fees)}` : ""}</div>
      {pack.errors?.length > 0 && <div className="text-amber-800" data-testid="menzies-pack-errors">{pack.errors.length} document{pack.errors.length === 1 ? "" : "s"} could not be fetched (listed in _not-included.txt inside the zip)</div>}
      {pack.loan_errors?.length > 0 && <div className="text-amber-800">LoanDisk figures unavailable — the summary used the file's balance</div>}
    </div>
  );
}

export default function MenziesPackDialog({ caseRow, onClose, onDone, onGate }) {
  const [st, setSt] = useState(null);
  const [picked, setPicked] = useState([]);
  const [extra, setExtra] = useState([]);
  const [notes, setNotes] = useState("");
  const [mail, setMail] = useState({ subject: "", body: "", cc: "" });
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(null);   // compile | send
  const [editMail, setEditMail] = useState(false);

  const load = async (keepPicks = false) => {
    try {
      const s = await getMenziesState(caseRow.case_id);
      setSt(s);
      if (!keepPicks) setPicked((s.candidates || []).filter((f) => f.suggested).map((f) => f.key));
      setMail((m) => ({ subject: m.subject || s.email?.subject || "", body: m.body || s.email?.body || "", cc: m.cc }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseRow.case_id]);

  const compile = async () => {
    setBusy("compile");
    try {
      const refs = await uploadAll(extra.map((f) => ({ file: f, kind: "other" })), setProgress);
      const r = await compileMenziesPack(caseRow.case_id, { keys: picked, notes, files: refs });
      (r.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Claim pack compiled — ${r.pack.files.length} documents, claim ${money(r.pack.claim_total)} · saved to OneDrive / Court`);
      setExtra([]);
      setSt(r.state);
      setMail((m) => ({ ...m, subject: r.state.email?.subject || m.subject, body: r.state.email?.body || m.body }));
      window.dispatchEvent(new CustomEvent("portal:files-changed"));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); setProgress(null); }
  };

  const send = async () => {
    if (!window.confirm(`Email the claim pack from ${st.email.from_mailbox} to ${st.email.to.join(", ")} now? This is a real email and moves the file to 'Referred to Menzies LLP'.`)) return;
    setBusy("send");
    try {
      const r = await sendMenziesPack(caseRow.case_id, { subject: mail.subject, body: mail.body, cc: mail.cc });
      toast.success(`Claim pack emailed to ${r.referral.to.join(", ")} · route → ${r.route?.label}`);
      onDone?.(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gate) { toast.error(errorDetailMessage(e, "")); onGate?.(d.gate.gate); }
      else toast.error(errMsg(e));
    } finally { setBusy(null); }
  };

  const gate = st?.gate;
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="menzies-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2 flex-wrap">
            <Package className="w-4 h-4 text-emerald-700" />Menzies claim pack — {caseRow.customer_name}
            {st && <span className="text-[11px] font-normal text-muted-foreground">· {st.route_label} · order {fmtDate(st.order?.order_date)}{st.order?.court_ref ? ` · ${st.order.court_ref}` : ""} · balance {money(st.balance)} · costs {money(st.costs?.recoverable)} recoverable</span>}
          </DialogTitle>
        </DialogHeader>
        {!st ? <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Reading the file and OneDrive…</div> : (
          <div className="space-y-3 text-xs">
            {st.referral && (
              <div className="border rounded p-2 bg-indigo-50/60 text-[11px]" data-testid="menzies-referral-done">
                <Mail className="w-3.5 h-3.5 inline mr-1 text-indigo-700" /><strong>Sent</strong> {fmtDate(st.referral.at)} by {st.referral.by} to {st.referral.to.join(", ")}{st.referral.cc?.length ? ` (cc ${st.referral.cc.join(", ")})` : ""} — "{st.referral.subject}" · {st.referral.attachments.map((a) => a.name).join(" + ")}{st.referral.linked_instead_of_attached ? " · zip shared by link" : ""}
              </div>
            )}
            {st.can_compile && (
              <div className="space-y-2" data-testid="menzies-compile-section">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-medium">1 · Documents for the pack <span className="font-normal text-muted-foreground">— suggested ticks from the file; the Creditor claim summary PDF is added automatically</span></div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setPicked(st.candidates.filter((f) => !f.is_pack_output).map((f) => f.key))} data-testid="menzies-pick-all">All</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setPicked(st.candidates.filter((f) => f.suggested).map((f) => f.key))} data-testid="menzies-pick-suggested">Suggested</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setPicked([])} data-testid="menzies-pick-none">None</Button>
                  </div>
                </div>
                <CandidateList candidates={st.candidates} categories={st.categories} picked={picked} setPicked={setPicked} disabled={!!busy} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Extra documents not on the file (optional)"><FileDrop files={extra} setFiles={setExtra} busy={!!busy} label="Add document" hint="PDF / images → into the zip" testId="menzies-extra" /></Field>
                  <Field label="Note on the claim summary (optional)"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-xs" data-testid="menzies-notes" /></Field>
                </div>
                <UploadProgress progress={progress} testId="menzies-upload-progress" />
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={compile} disabled={!!busy || (picked.length === 0 && extra.length === 0)} data-testid="menzies-compile">
                  {busy === "compile" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Package className="w-3.5 h-3.5 mr-1" />}{st.pack ? "Re-compile pack" : "Compile pack"} ({picked.length + extra.length})
                </Button>
              </div>
            )}
            <PackSummary pack={st.pack} />
            {st.can_compile && (
              <div className="space-y-2" data-testid="menzies-send-section">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[11px] font-medium">2 · Email to Menzies from {st.email.from_mailbox}</div>
                  {gate && (
                    <button type="button" onClick={() => onGate?.("G10")} data-testid="menzies-gate-G10"
                            className={cn("inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border", gate.passed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200")}>
                      {gate.passed ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}G10{!gate.passed && ` · ${gate.blocking.length} open`}
                    </button>
                  )}
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-auto" onClick={() => setEditMail((v) => !v)} data-testid="menzies-edit-mail">{editMail ? "Use template text" : "Edit this email"}</Button>
                </div>
                <div className="border rounded p-2 bg-slate-50/60 space-y-1.5">
                  <div className="text-[11px]" data-testid="menzies-to"><strong>To:</strong> {st.email.to.length ? st.email.to.join(", ") : <span className="text-rose-700">not set — Legal process settings → Notifications → Menzies email</span>}{st.email.cc?.length ? <span className="text-slate-500"> · cc {st.email.cc.join(", ")}</span> : null}</div>
                  <Field label="Extra cc (optional)"><Input value={mail.cc} onChange={(e) => setMail((m) => ({ ...m, cc: e.target.value }))} placeholder="name@simplyfunded.co.uk" className="h-7 text-xs max-w-md" data-testid="menzies-cc" /></Field>
                  {editMail ? (
                    <>
                      <Field label="Subject"><Input value={mail.subject} onChange={(e) => setMail((m) => ({ ...m, subject: e.target.value }))} className="h-7 text-xs" data-testid="menzies-subject" /></Field>
                      <Field label="Body"><Textarea rows={8} value={mail.body} onChange={(e) => setMail((m) => ({ ...m, body: e.target.value }))} className="text-xs" data-testid="menzies-body" /></Field>
                    </>
                  ) : (
                    <div className="text-[11px]" data-testid="menzies-preview"><div><strong>Subject:</strong> {st.email.subject}</div><pre className="whitespace-pre-wrap font-sans mt-1 text-slate-700">{st.email.body}</pre></div>
                  )}
                  <div className="text-[10px] text-muted-foreground">Attachments: Creditor claim summary PDF + the zip (shared as a OneDrive link when over {st.attach_cap_mb} MB).</div>
                </div>
                {st.send_blockers.length > 0 && <ul className="text-[11px] text-rose-800 list-disc pl-4" data-testid="menzies-blockers">{st.send_blockers.map((b) => <li key={b}>{b}</li>)}</ul>}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={!!busy} data-testid="menzies-close">Close</Button>
          {st?.can_compile && (
            <Button size="sm" className="bg-indigo-700 hover:bg-indigo-800" onClick={send} disabled={!!busy || !st.can_send} title={st.send_blockers.join("; ")} data-testid="menzies-send">
              {busy === "send" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />}Send to Menzies · route → Referred
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
